import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type { IInquiryRepository } from '@repositories/inquiryRepository/IInquiryRepository';
import type {
  InquiryCreateRq,
  ProductInquiryForm,
  ProductInquiryFormFilter,
} from '@repositories/inquiryRepository/inquiry.types';
import type { Page } from '@type/Page';

const inquiryRepo = repository.get<IInquiryRepository>(KEYS.INQUIRY_REPOSITORY);

const inquiryQueryKey = {
  all: ['inquiry'] as const,
  lists: () => [...inquiryQueryKey.all, 'list'] as const,
};

const inquiryFormQueryKey = {
  all: ['productInquiryForm'] as const,
  lists: () => [...inquiryFormQueryKey.all, 'list'] as const,
  list: (filter: ProductInquiryFormFilter) => [...inquiryFormQueryKey.lists(), { ...filter }] as const,
};

export const useGetProductInquiryForm = (
  filter: ProductInquiryFormFilter,
  options?: UseQueryOptions<Page<ProductInquiryForm>>,
) =>
  useQuery({
    queryKey: inquiryFormQueryKey.list(filter),
    queryFn: () => inquiryRepo.getProductInquiryForms(filter),
    ...options,
  });

export const useAddInquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InquiryCreateRq) => {
      const result = await inquiryRepo.create(data);
      return result.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inquiryQueryKey.lists() });
    },
  });
};
